// src/hooks/useUnreadNotificationCount.ts
// Hook for the shell adapter to get the unread notification count.
//
// Returns the shape expected by @levelup/ui-shell's UnreadCountResult.
//
// Data flows through the CRM BFF (`/api/platform/notifications/inbox/unread-count`),
// NOT browser-direct to Central — so it works in RP/BFF mode where the browser
// holds no Central token. The server derives the workspace from the session, so
// this hook passes NO workspaceId. Freshness is via bounded polling (PR-N1);
// the same-origin SSE proxy is a PR-N2 follow-up.

import { useGetPlatformUnreadCountQuery } from '../api/platformNotificationsApi';
import { APP_ID } from '../config/centralAuth';
import { NOTIFICATION_POLL_INTERVAL_MS } from '../config/notifications';
import type { UnreadCountResult } from '@levelup/ui-shell';

/**
 * Returns the unread notification count for the current app (CRM) in the format
 * expected by the shell adapter. Central is authoritative for the count.
 *
 * @returns UnreadCountResult - { count, isLoading?, isError? }
 */
export function useUnreadNotificationCount(): UnreadCountResult {
  const { data, isLoading, isError } = useGetPlatformUnreadCountQuery(
    { appId: APP_ID },
    { pollingInterval: NOTIFICATION_POLL_INTERVAL_MS },
  );

  return {
    // Central's canonical field is `total` (workspace-wide). Do not recompute
    // from `byApp`.
    count: data?.total ?? 0,
    isLoading,
    isError,
  };
}
